import React, { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import BlockIcon from "@mui/icons-material/Block";
import { fetchCertificateSends } from "../../api/apiClient";
import { reasonText } from "./SendCertificatesBar";

const POLL_MS = 4000;

const NEXT_STEP = {
  CLASS_NOT_COMPLETE: "Certificates go out once the class is marked complete and Send certificates is pressed on the roster.",
  NOT_ASSIGNED: "An instructor assigned to this class sends its certificates from the roster.",
};

/** How each status reads for one student, per certificate type. */
function describe(line, summary, typeName) {
  const aha = typeName.includes("AHA");
  switch (line.status) {
    case "SENT":
      return { icon: <CheckCircleIcon sx={{ color: "var(--color-success)" }} />, title: aha ? "AHA eCard issued" : "Certificate issued",
        detail: aha ? "The student claims it from their Certifications page." : "It's on the student's record, with its PDF." };
    case "SENDING":
      return { icon: <CircularProgress size={18} />, title: "On its way to AHA",
        detail: "Sent; waiting for AHA's answer. This updates by itself." };
    case "WILL_SEND":
      return { icon: <ScheduleIcon sx={{ color: "var(--color-accent-violet, #6b5bd2)" }} />,
        title: line.retry ? "Will be sent again" : summary.canSend ? "Ready to send" : "Will be sent",
        detail: summary.canSend ? "Goes out with the next Send certificates on the roster." : (NEXT_STEP[summary.notReady] || "") };
    case "FAILED":
      return { icon: <ErrorOutlineIcon color="error" />, title: aha ? "AHA didn't issue it" : "Couldn't be issued",
        detail: (line.reasons || []).map(reasonText).join(" ") || "It will be tried again with the next Send certificates." };
    case "UNKNOWN":
      return { icon: <HelpOutlineIcon color="warning" />, title: "Result unknown",
        detail: "AHA didn't confirm the card. A coordinator checks AHA's records and resolves it on the roster." };
    default:
      return { icon: <BlockIcon sx={{ color: "var(--color-ink-3, #8a8699)" }} />, title: "Not sent yet",
        detail: (line.reasons || []).map(reasonText).join(" ") };
  }
}

/**
 * Where this student's certificates for the class stand (platform certificate, AHA eCard, …), shown in the student
 * drawer after sign-off. Certificates are never sent from here: the instructor sends the whole class at once from the
 * roster once the class is complete. Re-reads while an AHA card is on its way. {@code fallback} shows when the class has
 * no certificate line for this student (nothing set up, or the summary is still empty).
 */
export default function StudentCertificatesStatus({ classId, userId, refreshKey, fallback = null }) {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(() => fetchCertificateSends(classId)
    .then((data) => { setSummary(data); setError(false); })
    .catch(() => setError(true)), [classId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const rows = (summary?.types || []).map((t) => ({
    type: t, line: (t.students || []).find((s) => String(s.userId) === String(userId)),
  })).filter((r) => r.line);
  const inFlight = rows.some((r) => r.line.status === "SENDING");
  useEffect(() => {
    if (!inFlight) return undefined;
    const timer = setTimeout(load, POLL_MS);
    return () => clearTimeout(timer);
  }, [inFlight, summary, load]);

  if (error) {
    return <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
      This student's certificate status couldn't be loaded.
    </Typography>;
  }
  if (!summary) return null;
  if (rows.length === 0) return fallback;

  return (
    <Box data-testid="student-certificates" sx={{ mb: 1.5, border: "1px solid var(--color-hairline-cloud, #e6e3ee)", borderRadius: "10px", overflow: "hidden" }}>
      <Typography sx={{ px: 1.5, py: 1, fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase",
        color: "text.secondary", bgcolor: "var(--color-surface-press-light, #f6f5f9)" }}>
        Certificates for this class
      </Typography>
      {rows.map(({ type, line }) => {
        const d = describe(line, summary, type.name);
        return (
          <Box key={type.type} data-testid={`student-certificate-${type.type}`}
            sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", px: 1.5, py: 1.25, borderTop: "1px solid var(--color-hairline-cloud, #e6e3ee)" }}>
            <Box sx={{ pt: "2px", display: "flex" }}>{d.icon}</Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{type.name} · {d.title}</Typography>
              {d.detail && <Typography variant="caption" color="text.secondary" component="div">{d.detail}</Typography>}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
